// @flow
import { klona } from "klona/full";

import LayoutHintCollection from "../layouthint/LayoutHintCollection";
import type { ModelOptions } from "../types";
import { getBasePathModularUI } from "../../constants";

export type BaseDehydrateData = {
  +data: Object,
  +contributions: Object,
  connectKey: string,
  ...
};

/**
 * Base model, this is the foundation of a model,
 * it contains data that is needed for all models like, data, contributions and layout hint
 *
 * Extend this for non modular ui resources, for modular ui resource start with the resourceModel
 */
class BaseModel {
  _data: Object;
  _contributions: Object;
  _layouthint: LayoutHintCollection;
  _connectKey: string;
  _modelOptions: ModelOptions;

  /**
   * constructor
   */
  constructor(
    data: Object,
    contributions: Object,
    // $FlowIssue
    modelOptions: ModelOptions = {},
  ) {
    this._data = data;
    this._contributions = contributions;
    this._layouthint = new LayoutHintCollection(
      this.getContribution("layouthint", []),
    );

    this._modelOptions = {
      origin: "",
      contextPath: getBasePathModularUI(),
      ...modelOptions,
    };
  }

  /**
   * Retrieve data
   */
  get data(): Object {
    return this._data || {};
  }

  /**
   * Retrieve property from the data object of the model, mostly used internal
   */
  getData(propName: string, defaultValue: any = null): any {
    return this.data[propName] ?? defaultValue;
  }

  /**
   * Retrieve contributions
   */
  get contributions(): Object {
    return this._contributions || {};
  }

  /**
   * Retrieve property from the contributions of the model, mostly used internal
   */
  getContribution(propName: string, defaultValue: any = null): any {
    return this.contributions[propName] ?? defaultValue;
  }

  /**
   */
  get modelOptions(): ModelOptions {
    return this._modelOptions;
  }

  /**
   */
  get origin(): string {
    return this.modelOptions.origin || "";
  }

  /**
   */
  get contextPath(): string {
    return this.modelOptions.contextPath || getBasePathModularUI();
  }

  /**
   * Getting the layouthint
   */
  get layouthint(): LayoutHintCollection {
    return this._layouthint;
  }

  /**
   * Set the layouthint
   */
  set layouthint(layouthint: Array<string>) {
    this._layouthint = new LayoutHintCollection(layouthint);
  }

  /**
   * Indicates if the model has data
   */
  get hasData(): boolean {
    return Object.keys(this.data).length > 0;
  }

  /**
   * Set the unique key for this model
   */
  set connectKey(key: string) {
    this._connectKey = key;
  }

  /**
   * Get the unique key of this model
   */
  get connectKey(): string {
    return this._connectKey;
  }

  /**
   * Dehydrate internal data, returns the information that is needed to recreate the model
   */
  dehydrate(): BaseDehydrateData {
    return {
      data: this._data,
      contributions: this._contributions,
      connectKey: this._connectKey,
      options: this._modelOptions,
    };
  }

  /**
   * Recreate the model with the given data
   */
  rehydrate(data: BaseDehydrateData) {
    this._connectKey = data.connectKey;
  }

  /**
   * Returns a clone of the model
   */
  clone(): any {
    return klona(this);
  }
}

export default BaseModel;
